import type {
  ResolvedToolDefinition,
  ToolCalloutGroup,
  ToolReferenceTable,
} from "@/data/tools";
import type { ToolIntroBlock } from "@/data/tools/types";
import Link from "next/link";
import ToolIcon from "./ToolIcon";

type ToolIntroSectionProps = {
  tool: ResolvedToolDefinition;
};

const iconColorMap = {
  primary: "text-[#5b32b4]",
  accent: "text-[#c3003a]",
} as const;

const dotColorMap = {
  primary: "bg-[#5b32b4]",
  accent: "bg-[#c3003a]",
} as const;

// ---------------------------------------------------------------------------
// Callout group
// ---------------------------------------------------------------------------

function renderCalloutGroup(group: ToolCalloutGroup) {
  if (group.layout === "cards") {
    return (
      <div>
        <p className="text-[11px] font-medium uppercase tracking-widest text-gray-500 mb-3">
          {group.title}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {group.items.map((item) => {
            const accent = item.accent ?? "primary";
            return (
              <div
                key={item.label}
                className="rounded-xl border border-gray-100 bg-white p-3"
              >
                <p
                  className={`text-[12px] font-medium mb-1 ${
                    accent === "accent" ? "text-[#c3003a]" : "text-[#5b32b4]"
                  }`}
                >
                  {item.href ? (
                    <Link href={item.href} className="hover:underline">
                      {item.label}
                    </Link>
                  ) : (
                    item.label
                  )}
                </p>
                {item.description ? (
                  <p className="text-[11.5px] text-gray-500 leading-relaxed">
                    {item.description}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-widest text-gray-500 mb-3">
        {group.title}
      </p>
      <ul className="flex flex-col gap-2.5">
        {group.items.map((item) => {
          const colorKey = item.accent ?? group.iconColor ?? "primary";
          return (
            <li key={item.label} className="flex items-start gap-2.5">
              {item.icon ? (
                <ToolIcon
                  icon={item.icon}
                  size={15}
                  className={`${iconColorMap[colorKey]} shrink-0 mt-0.5`}
                />
              ) : (
                <span className="shrink-0 mt-[0.35rem]">
                  <span
                    className={`block w-1.5 h-1.5 rounded-full ${dotColorMap[colorKey]}`}
                  />
                </span>
              )}
              <span>
                {item.href ? (
                  <Link
                    href={item.href}
                    className="text-[13px] font-medium text-gray-800 hover:text-[#5b32b4] hover:underline"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-[13px] font-medium text-gray-800">
                    {item.label}
                  </span>
                )}
                {item.description ? (
                  <span className="block text-[12px] text-gray-500 mt-0.5 leading-relaxed">
                    {item.description}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Callout shell
// ---------------------------------------------------------------------------

function renderCalloutShell(groups: readonly ToolCalloutGroup[]) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-5">
      <div
        className={`grid gap-6 ${
          groups.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"
        }`}
      >
        {groups.map((group) => (
          <div key={group.title}>{renderCalloutGroup(group)}</div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reference table
// ---------------------------------------------------------------------------

function renderReferenceTable(table: ToolReferenceTable) {
  const emphasisColumns = new Set(table.emphasisColumns ?? []);
  const monospaceColumns = new Set(table.monospaceColumns ?? []);

  return (
    <div
      key={table.title}
      className="rounded-xl border border-gray-100 overflow-hidden bg-white"
    >
      {/* header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
        <h3 className="text-[14px] font-medium text-gray-900">{table.title}</h3>
        {table.description ? (
          <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">
            {table.description}
          </p>
        ) : null}
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="bg-[#5b32b4]/5">
              {table.columns.map((column) => (
                <th
                  key={column}
                  className="text-left px-5 py-2.5 text-[10px] font-medium uppercase tracking-widest text-[#5b32b4] whitespace-nowrap border-b border-gray-100"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, index) => (
              <tr
                key={`${table.title}-${index}`}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50/60"}
              >
                {table.columns.map((column) => {
                  const isEmphasis = emphasisColumns.has(column);
                  const isMono = monospaceColumns.has(column);
                  return (
                    <td
                      key={column}
                      className={[
                        "px-5 py-3 align-top border-b border-gray-100 last-of-type:border-b-0 leading-relaxed",
                        isEmphasis
                          ? "font-medium text-[#c3003a]"
                          : "text-gray-500",
                        isMono ? "font-mono text-[11px] text-gray-700" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {row[column] ?? ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* note */}
      {table.note ? (
        <p className="px-5 py-3 text-[11px] text-gray-500 border-t border-gray-100 bg-gray-50">
          {table.note}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Text block
// ---------------------------------------------------------------------------

function renderTextBlock(
  block: Extract<ToolIntroBlock, { kind: "text" }>,
  index: number,
) {
  const HeadingTag = block.level === "h3" ? "h3" : "h2";
  const headingId =
    block.level === "h2" && index === 0 ? "tool-intro-heading" : undefined;

  return (
    <div key={`${block.kind}-${block.title}-${index}`} className="space-y-3">
      <HeadingTag
        id={headingId}
        className={
          block.level === "h3"
            ? "text-xl md:text-2xl font-semibold text-gray-900"
            : "text-2xl md:text-3xl font-semibold text-[#5b32b4]"
        }
      >
        {block.title}
      </HeadingTag>

      {block.paragraphs.map((paragraph) => (
        <p key={paragraph} className="text-[14px] text-gray-600 leading-[1.75]">
          {paragraph}
        </p>
      ))}

      {block.note ? (
        <p className="text-[12px] text-gray-500 border-l-2 border-[#5b32b4] bg-[#5b32b4]/[0.04] pl-3 py-2 rounded-r-lg">
          {block.note}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Code block
// ---------------------------------------------------------------------------

function renderCodeBlock(
  block: Extract<ToolIntroBlock, { kind: "code" }>,
  index: number,
) {
  return (
    <div
      key={`${block.kind}-${block.title}-${index}`}
      className="rounded-xl border border-gray-100 overflow-hidden"
    >
      <div className="bg-gray-50 px-5 py-3.5 border-b border-gray-100">
        <h3 className="text-[11px] font-medium uppercase tracking-widest text-[#5b32b4]">
          {block.title}
        </h3>
        {block.description ? (
          <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">
            {block.description}
          </p>
        ) : null}
      </div>
      <div className="bg-[#1e1530] px-5 py-4 overflow-x-auto">
        <pre className="text-[12px] text-[#c4b5d9] leading-relaxed font-mono whitespace-pre">
          <code>{block.code}</code>
        </pre>
      </div>
      {block.note ? (
        <p className="px-5 py-3 text-[11px] text-gray-500 bg-gray-50 border-t border-gray-100">
          {block.note}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block dispatcher
// ---------------------------------------------------------------------------

function renderIntroBlock(block: ToolIntroBlock, index: number) {
  if (block.kind === "text") {
    return renderTextBlock(block, index);
  }

  if (block.kind === "callouts") {
    return (
      <div key={`callouts-${index}`}>{renderCalloutShell(block.groups)}</div>
    );
  }

  if (block.kind === "code") {
    return renderCodeBlock(block, index);
  }

  // table
  return (
    <div key={`table-${index}`} className="space-y-5">
      {block.tables.map((table) => renderReferenceTable(table))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export default function ToolIntroSection({ tool }: ToolIntroSectionProps) {
  const intro = tool.pageContent?.intro;
  const callouts = tool.pageContent?.callouts;
  const tables = tool.pageContent?.referenceTables ?? [];
  const listGroups: ToolCalloutGroup[] = [...(callouts ?? [])];
  const introBlocks = tool.pageContent?.introBlocks;

  const supplementalBlocks: ToolIntroBlock[] = [
    ...(intro
      ? [
          {
            kind: "text" as const,
            title: intro.title,
            paragraphs: intro.paragraphs,
            note: intro.note,
            level: "h2" as const,
          },
        ]
      : []),
    ...(listGroups.length
      ? [
          {
            kind: "callouts" as const,
            groups: listGroups,
          } satisfies ToolIntroBlock,
        ]
      : []),
    ...(tables.length
      ? [{ kind: "table" as const, tables } satisfies ToolIntroBlock]
      : []),
  ];

  const primaryBlocks = introBlocks ?? [];

  if (!primaryBlocks.length && !supplementalBlocks.length) {
    return null;
  }

  return (
    <section
      className="max-w-5xl mx-auto space-y-8"
      aria-labelledby="tool-intro-heading"
    >
      {primaryBlocks.map((block, index) => renderIntroBlock(block, index))}
      {supplementalBlocks.map((block, index) =>
        renderIntroBlock(block, primaryBlocks.length + index),
      )}
    </section>
  );
}
